import Keycloak from "keycloak-js";
import type {
  KeycloakConfig,
  KeycloakInitOptions,
  KeycloakProfile,
} from "keycloak-js";

import {
  clearStoredAccessToken,
  clearStoredKioskOffline,
  getStoredAccessToken,
  getStoredKioskOfflineToken,
  setStoredAccessToken,
  setStoredKioskOfflineToken,
} from "@/features/auth/utils/auth-storage";

const KEYCLOAK_AUTO_LOGIN_DISABLED_KEY = "keycloak_auto_login_disabled";

/**
 * DriverRoom kiosk offline session (GitLab #94).
 *
 * A shared TV must stay logged in across reboots without a human re-login, without
 * a URL param, and WITHOUT affecting normal users. The kiosk is identified by a
 * Keycloak realm role and kept alive with an `offline_access` refresh token that is
 * persisted and rehydrated. Every branch below is entered ONLY when a kiosk signal
 * is true (`hasStoredKioskOffline()` pre-login, or `isKioskFromToken()` post-login);
 * a normal user never has the stored token nor the role, so they hit the exact
 * pre-existing init/login/logout/refresh code paths.
 */
const KIOSK_ROLE = "kiosk";

/** sessionStorage guard so the one-time offline upgrade can't loop through redirects. */
const KIOSK_OFFLINE_UPGRADE_ATTEMPTED_KEY = "kiosk_offline_upgrade_attempted";

/** Keycloak PKCE (S256) needs `crypto.subtle`, which browsers only expose on HTTPS or localhost. */
export function getKeycloakSecureContextError(): string | null {
  if (typeof window === "undefined") return null;
  if (window.isSecureContext && window.crypto?.subtle != null) return null;
  return (
    "Sign-in requires a secure connection (HTTPS). " +
    "This site is being served over plain HTTP, so the browser blocks Web Crypto. " +
    "Deploy the planner behind TLS (e.g. https://portal.staging.banffairporter.com/) or use http://localhost for local builds."
  );
}

function assertSecureContextForKeycloak(): void {
  const message = getKeycloakSecureContextError();
  if (message) {
    throw new Error(message);
  }
}

function getKeycloakConfig(): KeycloakConfig {
  return {
    url: import.meta.env.VITE_KEYCLOAK_URL ?? "http://localhost:8080",
    realm: import.meta.env.VITE_KEYCLOAK_REALM ?? "BAST",
    clientId:
      import.meta.env.VITE_KEYCLOAK_CLIENT_ID ??
      "com.banffairporter.planner-staging-web",
  };
}

const keycloak = new Keycloak(getKeycloakConfig());

let initPromise: Promise<boolean> | null = null;

function isAutoLoginSuppressed(): boolean {
  try {
    return (
      window.sessionStorage.getItem(KEYCLOAK_AUTO_LOGIN_DISABLED_KEY) === "true"
    );
  } catch {
    return false;
  }
}

function suppressKeycloakAutoLogin(): void {
  try {
    window.sessionStorage.setItem(KEYCLOAK_AUTO_LOGIN_DISABLED_KEY, "true");
  } catch {
    /* ignore private mode / storage access issues */
  }
}

function resumeKeycloakAutoLogin(): void {
  try {
    window.sessionStorage.removeItem(KEYCLOAK_AUTO_LOGIN_DISABLED_KEY);
  } catch {
    /* ignore private mode / storage access issues */
  }
}

/**
 * Post-login kiosk signal: the current user carries the Keycloak realm role
 * `kiosk`. Safe to call any time; returns false when there is no parsed token.
 */
export function isKioskFromToken(): boolean {
  try {
    return (
      keycloak.tokenParsed?.realm_access?.roles?.includes(KIOSK_ROLE) === true
    );
  } catch {
    return false;
  }
}

/**
 * Pre-login kiosk signal: this browser has a persisted kiosk offline refresh
 * token. Presence means "resume as the kiosk"; absence means "normal browser".
 */
export function hasStoredKioskOffline(): boolean {
  try {
    return getStoredKioskOfflineToken() !== null;
  } catch {
    return false;
  }
}

/** Decodes a JWT payload (middle segment) without verification. Null on any failure. */
function decodeJwtPayload(
  token: string | undefined,
): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const segments = token.split(".");
    if (segments.length < 2) return null;
    const base64 = segments[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(
      base64.length + ((4 - (base64.length % 4)) % 4),
      "=",
    );
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** A Keycloak refresh token is an offline token when its JWT `typ` claim is "Offline". */
function isOfflineRefreshToken(token: string | undefined): boolean {
  try {
    return decodeJwtPayload(token)?.typ === "Offline";
  } catch {
    return false;
  }
}

function isCurrentRefreshTokenOffline(): boolean {
  return isOfflineRefreshToken(keycloak.refreshToken);
}

/** Persists the CURRENT keycloak refresh (+ id) token as the kiosk offline session. */
function persistKioskOfflineFromCurrent(): void {
  try {
    const refreshToken = keycloak.refreshToken?.trim();
    if (!refreshToken) return;
    setStoredKioskOfflineToken(refreshToken, keycloak.idToken?.trim());
  } catch {
    /* never throw out of the auth bootstrap */
  }
}

function isKioskOfflineUpgradeAttempted(): boolean {
  try {
    return (
      window.sessionStorage.getItem(KIOSK_OFFLINE_UPGRADE_ATTEMPTED_KEY) ===
      "true"
    );
  } catch {
    return false;
  }
}

function setKioskOfflineUpgradeAttempted(): void {
  try {
    window.sessionStorage.setItem(KIOSK_OFFLINE_UPGRADE_ATTEMPTED_KEY, "true");
  } catch {
    /* ignore private mode / storage access issues */
  }
}

function clearKioskOfflineUpgradeGuard(): void {
  try {
    window.sessionStorage.removeItem(KIOSK_OFFLINE_UPGRADE_ATTEMPTED_KEY);
  } catch {
    /* ignore private mode / storage access issues */
  }
}

function syncStoredToken(): void {
  const token = keycloak.token?.trim();
  if (token) {
    setStoredAccessToken(token);
  } else {
    clearStoredAccessToken();
  }

  // Re-persist on rotation: a kiosk offline session may get a rotated refresh
  // token on every refresh. Losing the rotated one would break the next refresh
  // (and the next reboot), so re-save it whenever this is a kiosk offline session.
  // Gated: a normal user has neither a stored kiosk token nor an offline refresh
  // token, so this block is a no-op for them and the access-token sync above is
  // byte-for-byte the original behavior.
  try {
    if (hasStoredKioskOffline() || isCurrentRefreshTokenOffline()) {
      persistKioskOfflineFromCurrent();
    }
  } catch {
    /* never throw out of token sync */
  }
}

export async function initializeKeycloak(): Promise<boolean> {
  if (initPromise) return initPromise;

  assertSecureContextForKeycloak();

  // Kiosk rehydrate path (GitLab #94). ONLY entered when this browser has a
  // persisted kiosk offline refresh token. A normal browser has no such token,
  // so it skips straight to runNormalInit() — the original code path unchanged.
  initPromise = hasStoredKioskOffline()
    ? initializeKioskFromOffline()
    : runNormalInit();

  return initPromise;
}

/**
 * The pre-existing init flow. For a non-kiosk user the only added work is one
 * `isKioskFromToken()` check (false), so the observable behavior — onLoad mode,
 * auto-login resume, token sync — is identical to before this feature.
 */
async function runNormalInit(): Promise<boolean> {
  const options: KeycloakInitOptions = {
    onLoad: isAutoLoginSuppressed() ? "check-sso" : "login-required",
    pkceMethod: "S256",
    checkLoginIframe: false,
  };

  const authenticated = await keycloak.init(options);
  if (authenticated) {
    resumeKeycloakAutoLogin();
  }
  syncStoredToken();

  // One-time offline upgrade for a first-time kiosk login (kiosk role only).
  if (authenticated && isKioskFromToken()) {
    await maybeUpgradeKioskToOffline();
  }
  return authenticated;
}

/**
 * Rehydrate the kiosk session from the persisted offline refresh token so the TV
 * survives reboots / dead SSO cookies without an interactive redirect. On any
 * failure (offline token revoked/expired) the stored token is cleared and we fall
 * through to the normal init path, so the kiosk simply becomes a normal login.
 */
async function initializeKioskFromOffline(): Promise<boolean> {
  const stored = getStoredKioskOfflineToken();
  try {
    await keycloak.init({
      onLoad: "check-sso",
      pkceMethod: "S256",
      checkLoginIframe: false,
      refreshToken: stored?.refreshToken,
      token: getStoredAccessToken(),
      idToken: stored?.idToken,
    });

    // Force-mint a fresh access token from the offline refresh token.
    await keycloak.updateToken(-1);

    if (keycloak.authenticated) {
      resumeKeycloakAutoLogin();
      // Persist the (possibly rotated) refresh token, then sync the access token.
      persistKioskOfflineFromCurrent();
      syncStoredToken();
      return true;
    }

    // Not authenticated after rehydrate — treat as a dead offline session.
    clearStoredKioskOffline();
    return runNormalInit();
  } catch {
    clearStoredKioskOffline();
    return runNormalInit();
  }
}

/**
 * First-time kiosk setup: the current refresh token is a normal (online) token,
 * so redirect once through Keycloak requesting `offline_access`. The SSO cookie
 * already exists, so this returns silently. On the return trip the refresh token
 * is an Offline token and gets persisted; a session guard prevents redirect loops.
 */
async function maybeUpgradeKioskToOffline(): Promise<void> {
  try {
    // Already an offline session — persist it and we're done (no re-upgrade).
    if (isCurrentRefreshTokenOffline()) {
      persistKioskOfflineFromCurrent();
      clearKioskOfflineUpgradeGuard();
      return;
    }

    // Only attempt the redirect once per browser session.
    if (isKioskOfflineUpgradeAttempted()) return;
    setKioskOfflineUpgradeAttempted();

    await keycloak.login({
      scope: "openid offline_access",
      redirectUri: window.location.href,
    });
  } catch {
    /* never throw out of the auth bootstrap */
  }
}

export async function loginToKeycloak(): Promise<void> {
  assertSecureContextForKeycloak();
  resumeKeycloakAutoLogin();
  await keycloak.login({
    redirectUri: window.location.href,
  });
}

export function getKeycloakAccessToken(): string | undefined {
  return keycloak.token?.trim() || undefined;
}

export function getKeycloakDisplayName(): string | null {
  const parsed = keycloak.tokenParsed;
  if (!parsed) return null;

  const fullName =
    typeof parsed.name === "string" && parsed.name.trim().length > 0
      ? parsed.name.trim()
      : null;
  if (fullName) return fullName;

  const preferred =
    typeof parsed.preferred_username === "string" &&
    parsed.preferred_username.trim().length > 0
      ? parsed.preferred_username.trim()
      : null;
  if (preferred) return preferred;

  const email =
    typeof parsed.email === "string" && parsed.email.trim().length > 0
      ? parsed.email.trim()
      : null;
  return email;
}

export async function loadKeycloakProfile(): Promise<KeycloakProfile | null> {
  if (!keycloak.authenticated) return null;
  try {
    return await keycloak.loadUserProfile();
  } catch {
    return null;
  }
}

/** Proactive refresh threshold (seconds) before outbound API calls. */
export const API_ACCESS_TOKEN_MIN_VALIDITY_SECONDS = 15;

export async function refreshKeycloakToken(
  minValiditySeconds: number = 30,
): Promise<boolean> {
  if (!keycloak.authenticated) return false;
  try {
    const refreshed = await keycloak.updateToken(minValiditySeconds);
    syncStoredToken();
    return refreshed;
  } catch {
    clearKeycloakSession();
    return false;
  }
}

let proactiveRefreshInFlight: Promise<boolean> | null = null;
let forceRefreshInFlight: Promise<boolean> | null = null;

function runSingleFlight(
  slot: "proactive" | "force",
  operation: () => Promise<boolean>,
): Promise<boolean> {
  if (slot === "proactive") {
    if (!proactiveRefreshInFlight) {
      proactiveRefreshInFlight = operation().finally(() => {
        proactiveRefreshInFlight = null;
      });
    }
    return proactiveRefreshInFlight;
  }

  if (!forceRefreshInFlight) {
    forceRefreshInFlight = operation().finally(() => {
      forceRefreshInFlight = null;
    });
  }
  return forceRefreshInFlight;
}

/**
 * Ensures the access token is valid for at least `minValiditySeconds` before an API
 * call. Concurrent callers share one refresh (single-flight), matching proactive
 * Dio-style interceptors.
 */
export async function ensureAccessTokenFreshForApiCall(
  minValiditySeconds: number = API_ACCESS_TOKEN_MIN_VALIDITY_SECONDS,
): Promise<void> {
  if (!keycloak.authenticated) return;
  await runSingleFlight("proactive", () =>
    refreshKeycloakToken(minValiditySeconds),
  );
}

/**
 * Forces a Keycloak token refresh after the API returned 401. Used for reactive
 * retry (e.g. revoked access token, clock skew). Returns false when refresh fails.
 */
export async function forceRefreshAccessTokenAfterUnauthorized(): Promise<boolean> {
  if (!keycloak.authenticated) return false;
  return runSingleFlight("force", () => refreshKeycloakToken(-1));
}

export async function logoutFromKeycloak(): Promise<void> {
  suppressKeycloakAutoLogin();
  clearStoredAccessToken();
  // Wipe the kiosk offline session so the same browser is clean for the next
  // (normal) user — after logout hasStoredKioskOffline() is false, so the next
  // visitor gets the normal login screen and is never auto-resumed as the kiosk.
  clearStoredKioskOffline();
  clearKioskOfflineUpgradeGuard();
  initPromise = null;
  await keycloak.logout({
    redirectUri: window.location.href,
  });
}

export function clearKeycloakSession(): void {
  clearStoredAccessToken();
  clearStoredKioskOffline();
  keycloak.clearToken();
  initPromise = null;
}

export { keycloak };
